import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  Button,
  ListGroup,
  Form,
  Alert,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { Transaction } from "@mysten/sui/transactions";
import { bcs } from "@mysten/bcs";
import { useCurrentAccount, useSignAndExecuteTransaction, useSuiClient } from "@mysten/dapp-kit";
import Long from "long";
import { isMobileDevice } from "./utils/mobileWallet";
import { encryptMessage, decryptMessage } from "./utils/encryption";
import { storeOnWalrus, retrieveFromWalrus } from "./utils/walrus";
import { storeOnIPFS, retrieveFromIPFS } from "./utils/ipfs";
import logger from './utils/logger';
import './Chat.css';
import { RiMessage2Line, RiCloseLine, RiArrowDownLine, RiSendPlaneLine, RiTimeLine, RiCheckLine, RiErrorWarningLine, RiPhoneLine } from 'react-icons/ri';
import { useCall } from './context/CallContext';

// Simple key derivation from addresses (for testing - in production use proper key exchange)
function deriveSharedKeyFromAddresses(address1, address2) {
  // Sort addresses to ensure consistent key derivation regardless of order
  const [addr1, addr2] = [address1, address2].sort();

  // Create a deterministic key from the address pair
  const combined = addr1 + addr2 + 'su-messaging-salt';

  // Use Web Crypto API to hash and get raw bytes
  const encoder = new TextEncoder();
  const data = encoder.encode(combined);

  return crypto.subtle.digest('SHA-256', data).then(hash => {
    return new Uint8Array(hash);
  });
}

function Chat() {
  const { id: recipientAddress } = useParams();
  const navigate = useNavigate();
  const currentAccount = useCurrentAccount();
  const { startCall } = useCall();
  const isConnected = !!currentAccount;
  const { mutate: signAndExecuteTransactionBlock } = useSignAndExecuteTransaction();
  const client = useSuiClient();
  
  // Debug logging for component state
  useEffect(() => {
    logger.log('Chat component state:', { isConnected, currentAccount: currentAccount?.address, recipientAddress });
  }, [isConnected, currentAccount?.address, recipientAddress]);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [sendStatus, setSendStatus] = useState(false);
  const [error, setError] = useState(null);
  const [recipientName, setRecipientName] = useState(
    recipientAddress || "Stranger"
  );
  const [recentChats, setRecentChats] = useState([]);
  const [senderNames, setSenderNames] = useState(new Map()); // Map of address -> display name
  const chatContentRef = useRef(null);
  const lastMessageFetchTimeRef = useRef(null);
  const lastChatsFetchTimeRef = useRef(null);
  const [showMobileChatList, setShowMobileChatList] = useState(false); // Toggle for mobile chat list
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth < 768;
  const packageId =
    "0x2aaad5d1ce7482b5850dd11642358bf23cb0e6432b12a581eb77d212dca54045";

  // Reactive window resize listener
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Utility function for consistent message sorting
  const sortMessagesByTimestamp = (messages) => {
    return [...messages].sort((a, b) => {
      const timeA = a.clientTimestampMs || a.timestampMs;
      const timeB = b.clientTimestampMs || b.timestampMs;
      return timeA - timeB;
    });
  };

  // Helper function to decrypt a message from Walrus, IPFS, or Sui fallback
  const decryptMessageFromStorage = async (metadataString, senderAddress, recipientAddress) => {
    try {
      const metadata = JSON.parse(metadataString);

      if (metadata.version === "sui-compressed-v1" && metadata.compressedData) {
        // Ultimate fallback: compressed data stored on Sui
        logger.log("Decrypting from Sui compressed fallback storage");
        const encryptedData = atob(metadata.compressedData); // Decompress
        const sharedKey = await deriveSharedKeyFromAddresses(senderAddress, recipientAddress);
        const decryptedContent = await decryptMessage(encryptedData, sharedKey);
        return decryptedContent;
      } else if (metadata.version === "ipfs-fallback-v1" && metadata.cid) {
        // IPFS decentralized fallback
        logger.log("Decrypting from IPFS decentralized fallback, CID:", metadata.cid);
        const encryptedData = await retrieveFromIPFS(metadata.cid);
        const sharedKey = await deriveSharedKeyFromAddresses(senderAddress, recipientAddress);
        const decryptedContent = await decryptMessage(encryptedData, sharedKey);
        return decryptedContent;
      } else if (metadata.version === "sui-fallback-v1" && metadata.encryptedData) {
        // Legacy fallback: encrypted data stored directly on Sui
        logger.log("Decrypting from Sui fallback storage");
        const sharedKey = await deriveSharedKeyFromAddresses(senderAddress, recipientAddress);
        const decryptedContent = await decryptMessage(metadata.encryptedData, sharedKey);
        return decryptedContent;
      } else if (metadata.version === "walrus-v1" && metadata.blobId) {
        // Normal Walrus storage
        logger.log("Decrypting from Walrus storage, blobId:", metadata.blobId);
        const encryptedData = await retrieveFromWalrus(metadata.blobId);
        const sharedKey = await deriveSharedKeyFromAddresses(senderAddress, recipientAddress);
        const decryptedContent = await decryptMessage(encryptedData, sharedKey);
        return decryptedContent;
      } else {
        // Fallback for old format (plain text)
        return metadataString;
      }
    } catch (error) {
      logger.error('Failed to decrypt message from storage:', error);
      // Return a placeholder indicating decryption failed
      return "[Message temporarily unavailable - stored securely]";
    }
  };

  // Check if package exists
  useEffect(() => {
    const checkPackage = async () => {
      try {
        logger.log('Checking if package exists:', packageId);
        const packageInfo = await client.getObject({ id: packageId });
        logger.log('Package info:', packageInfo);
      } catch (error) {
        logger.error('Package not found or error:', error);
      }
    };
    if (client) checkPackage();
  }, [client]);

  // Define functions before use
  const fetchUserName = useCallback(
    async (address) => {
      try {
        const objects = await client.getOwnedObjects({
          owner: address,
          options: { showType: true, showContent: true },
        });
        const userObject = objects.data.find((obj) =>
          obj.data.type.includes(
            `${packageId}::su_messaging::User`
          )
        );
        const displayName = userObject?.data.content.fields.display_name
          ? new TextDecoder().decode(
              new Uint8Array(userObject.data.content.fields.display_name)
            )
          : address.slice(0, 6) + "...";
        
        // Update the sender names map
        setSenderNames(prev => new Map(prev.set(address, displayName)));
        
        return displayName;
      } catch (err) {
        logger.error("Failed to fetch username:", err);
        const fallbackName = address.slice(0, 6) + "...";
        setSenderNames(prev => new Map(prev.set(address, fallbackName)));
        return fallbackName;
      }
    },
    [client, packageId]
  );

  const scrollToBottom = useCallback(() => {
    if (chatContentRef.current) {
      chatContentRef.current.scrollTop = chatContentRef.current.scrollHeight;
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    logger.log('fetchMessages called with:', { isConnected, currentAccount: !!currentAccount, recipientAddress });
    if (!isConnected || !currentAccount || !recipientAddress) {
      logger.log('fetchMessages: missing requirements', { isConnected, currentAccount: !!currentAccount, recipientAddress });
      return;
    }

    // Skip if we recently fetched to avoid rate limits
    const now = Date.now();
    if (lastMessageFetchTimeRef.current && now - lastMessageFetchTimeRef.current < 5000) { // 5 second minimum interval
      return;
    }
    lastMessageFetchTimeRef.current = now;

    try {
      logger.log('fetchMessages: starting fetch for conversation between', currentAccount.address, 'and', recipientAddress);
      const senderAddress = currentAccount.address;
      
      // Try multiple approaches to fetch messages
      
      // Approach 1: Query for Message objects directly using dynamic field queries
      let fetchedMessages = [];
      const seenIds = new Set();
      
      try {
        // Query events to find message IDs, then fetch the objects
        const eventsResponse = await client.queryEvents({
          query: { MoveModule: { package: packageId, module: "su_messaging" } },
          limit: 200,
          order: "descending", // Get most recent first
        });
        
        logger.log('Events query successful, found', eventsResponse.data.length, 'events');
        
        // Filter for MessageCreated events
        const messageEvents = eventsResponse.data.filter(event => 
          event.type === `${packageId}::su_messaging::MessageCreated` ||
          event.type.includes('MessageCreated')
        );
        
        logger.log('Found', messageEvents.length, 'MessageCreated events');
        
        for (const event of messageEvents) {
          const eventData = event.parsedJson || {};
          const { message_id, sender, recipient } = eventData;
          
          if (!message_id || !sender || !recipient) continue;
          
          // Only process messages between these two users
          if (
            (sender === senderAddress && recipient === recipientAddress) ||
            (sender === recipientAddress && recipient === senderAddress)
          ) {
            try {
              // Get the message object
              const messageObject = await client.getObject({
                id: message_id,
                options: { showContent: true, showType: true },
              });
              
              if (messageObject.data?.content?.fields && !seenIds.has(message_id)) {
                const fields = messageObject.data.content.fields;
                const timestampMs = Long.fromValue(fields.timestamp).toNumber();
                const timestamp = new Date(timestampMs);
                
                // Decode the content
                const content = new TextDecoder().decode(
                  new Uint8Array(fields.encrypted_content || [])
                );
                
                logger.log('Fetched message:', {
                  id: message_id,
                  sender: fields.sender,
                  recipient: fields.recipient,
                  content: content,
                  timestamp: timestamp
                });
                
                fetchedMessages.push({
                  id: message_id,
                  sender: fields.sender,
                  recipient: fields.recipient,
                  content: content, // This is metadata that needs decryption
                  timestamp: timestamp,
                  timestampMs: timestampMs,
                  clientTimestampMs: timestampMs,
                  needsDecryption: true, // Mark for async decryption
                });
                seenIds.add(message_id);
              }
            } catch (err) {
              logger.error('Failed to fetch message object:', message_id, err);
            }
          }
        }
      } catch (eventError) {
        logger.error('Event-based fetching failed:', eventError);
        
        // Approach 2: Fallback - try to query owned objects for both users
        logger.log('Trying fallback approach: query owned objects');
        
        const addressesToCheck = [senderAddress, recipientAddress];
        
        for (const address of addressesToCheck) {
          try {
            const ownedObjects = await client.getOwnedObjects({
              owner: address,
              options: { showType: true, showContent: true },
              limit: 50,
            });
            
            // Look for Message objects
            const messageObjects = ownedObjects.data.filter(obj =>
              obj.data?.type?.includes(`${packageId}::su_messaging::Message`)
            );
            
            logger.log(`Found ${messageObjects.length} Message objects owned by ${address.slice(0, 6)}...`);
            
            for (const obj of messageObjects) {
              if (obj.data?.content?.fields && !seenIds.has(obj.data.objectId)) {
                const fields = obj.data.content.fields;
                
                // Check if this message is between our two users
                if (
                  (fields.sender === senderAddress && fields.recipient === recipientAddress) ||
                  (fields.sender === recipientAddress && fields.recipient === senderAddress)
                ) {
                  const timestampMs = Long.fromValue(fields.timestamp).toNumber();
                  const timestamp = new Date(timestampMs);
                  
                  const encryptedContent = new TextDecoder().decode(
                    new Uint8Array(fields.encrypted_content || [])
                  );

                  // For now, store the metadata - we'll decrypt asynchronously
                  fetchedMessages.push({
                    id: obj.data.objectId,
                    sender: fields.sender,
                    recipient: fields.recipient,
                    content: encryptedContent, // This is actually metadata now
                    timestamp: timestamp,
                    timestampMs: timestampMs,
                    clientTimestampMs: timestampMs,
                    needsDecryption: true, // Flag to indicate this needs decryption
                  });
                  seenIds.add(obj.data.objectId);
                }
              }
            }
          } catch (ownedError) {
            logger.error(`Failed to query owned objects for ${address}:`, ownedError);
          }
        }
      }
      
      // Sort messages by timestamp
      fetchedMessages.sort((a, b) => a.timestampMs - b.timestampMs);
      
      // Merge confirmed messages with optimistic messages, removing duplicates
      setMessages(prevMessages => {
        const confirmedMessages = fetchedMessages;
        
        // Separate optimistic messages from confirmed ones
        const optimisticMessages = prevMessages.filter(msg => msg.id.startsWith('temp-'));
        const existingConfirmedMessages = prevMessages.filter(msg => !msg.id.startsWith('temp-'));
        
        // Remove optimistic messages that have been confirmed (using client timestamps)
        const remainingOptimisticMessages = optimisticMessages.filter(optimistic => 
          !confirmedMessages.some(confirmed =>
            confirmed.sender === optimistic.sender &&
            confirmed.recipient === optimistic.recipient &&
            confirmed.content.trim() === optimistic.content.trim() &&
            Math.abs((confirmed.clientTimestampMs || confirmed.timestampMs) - (optimistic.clientTimestampMs || optimistic.timestampMs)) < 60000 // Within 1 minute
          )
        );
        
        // Combine all messages
        const allMessages = [...existingConfirmedMessages, ...confirmedMessages, ...remainingOptimisticMessages];

        // Simple deduplication by message ID - IDs should be unique
        const uniqueMessages = [];
        const seenIds = new Set();

        for (const message of allMessages) {
          if (!seenIds.has(message.id)) {
            uniqueMessages.push(message);
            seenIds.add(message.id);
          }
        }

        // Sort by client timestamp (when send button was clicked)
        return sortMessagesByTimestamp(uniqueMessages);
      });
    } catch (err) {
      setError("Failed to fetch messages: " + err.message);
      logger.error("Fetch error details:", err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, currentAccount, recipientAddress, client, scrollToBottom]);

  const fetchRecentChats = useCallback(async () => {
    if (!isConnected || !currentAccount) return;
    
    // Skip if we recently fetched to avoid rate limits
    const now = Date.now();
    if (lastChatsFetchTimeRef.current && now - lastChatsFetchTimeRef.current < 10000) { // 10 second minimum interval
      return;
    }
    lastChatsFetchTimeRef.current = now;
    
    try {
      const senderAddress = currentAccount.address;
      const recipients = new Set();
      
      // Try to get recent chats from events first
      try {
        const eventsResponse = await client.queryEvents({
          query: { MoveModule: { package: packageId, module: "su_messaging" } },
          limit: 200,
          order: "descending",
        });
        
        // Filter for MessageCreated events
        const messageEvents = eventsResponse.data.filter(event => 
          event.type === `${packageId}::su_messaging::MessageCreated` ||
          event.type.includes('MessageCreated')
        );
        
        for (const event of messageEvents) {
          const { sender, recipient } = event.parsedJson || {};
          if (sender === senderAddress) recipients.add(recipient);
          else if (recipient === senderAddress) recipients.add(sender);
        }
      } catch (eventError) {
        logger.error('Event-based chat fetching failed:', eventError);
        
        // Fallback: check localStorage for recent conversations
        logger.log('Trying localStorage fallback for recent chats');
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith(`messages_${senderAddress}_`)) {
            const recipientAddress = key.replace(`messages_${senderAddress}_`, '');
            recipients.add(recipientAddress);
          }
          // Also check for new conversation markers written by handleSend
          if (key && key.startsWith(`conversation_${senderAddress}_`)) {
            const recipientAddress = key.replace(`conversation_${senderAddress}_`, '');
            recipients.add(recipientAddress);
          }
        }
      }

      const recipientList = Array.from(recipients);
      logger.log('Found recent chat recipients:', recipientList);
      
      const chatData = await Promise.all(
        recipientList.map(async (address) => ({
          address,
          displayName: await fetchUserName(address),
        }))
      );
      setRecentChats(chatData);
    } catch (err) {
      logger.error("Failed to fetch recent chats:", err);
    }
  }, [isConnected, currentAccount, client, fetchUserName]);

  useEffect(() => {
    logger.log('useEffect triggered for fetchMessages with:', { isConnected, currentAccount: !!currentAccount, recipientAddress });
    fetchMessages();
    fetchRecentChats();
    const fetchNames = async () => {
      if (isConnected && currentAccount && recipientAddress) {
        setRecipientName(await fetchUserName(recipientAddress));
      }
    };
    fetchNames();
  }, [
    isConnected,
    currentAccount,
    recipientAddress,
    fetchMessages,
    fetchRecentChats,
    fetchUserName,
  ]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messages.length > 0) {
      // Small delay to ensure DOM has updated
      const timeoutId = setTimeout(() => {
        scrollToBottom();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [messages, isConnected, currentAccount?.address, recipientAddress, scrollToBottom]);

  // Continuously poll for new messages (always-on, throttled by fetchMessages)
  useEffect(() => {
    if (!isConnected || !recipientAddress) return;

    const interval = setInterval(() => {
      fetchMessages();
    }, 10000); // Poll every 10 seconds for new incoming messages

    return () => clearInterval(interval);
  }, [isConnected, recipientAddress, fetchMessages]);

  // Load messages from localStorage on component mount (only as fallback)
  useEffect(() => {
    if (isConnected && currentAccount && recipientAddress) {
      const storageKey = `messages_${currentAccount.address}_${recipientAddress}`;
      const storedMessages = localStorage.getItem(storageKey);
      if (storedMessages) {
        try {
          const parsedMessages = JSON.parse(storedMessages)
            .map(msg => ({
              ...msg,
              timestamp: msg.timestamp instanceof Date ? msg.timestamp : new Date(msg.timestamp),
              timestampMs: msg.timestampMs || Date.parse(msg.timestamp),
              clientTimestampMs: msg.clientTimestampMs || msg.timestampMs || Date.parse(msg.timestamp), // Ensure clientTimestampMs exists
            }))
            .filter(msg => {
              // Filter out old optimistic messages (older than 5 minutes)
              if (msg.id.startsWith('temp-') && Date.now() - (msg.clientTimestampMs || msg.timestampMs) > 300000) {
                return false;
              }
              return true;
            });
          
          // Only load from localStorage if we don't have messages yet (prevent overriding blockchain data)
          setMessages(prevMessages => {
            if (prevMessages.length === 0) {
              logger.log('Loading messages from localStorage:', parsedMessages.length, 'for key:', storageKey);
              logger.log('Sample messages:', parsedMessages.slice(0, 2).map(m => ({
                content: m.content.substring(0, 30),
                time: new Date(m.clientTimestampMs || m.timestampMs).toLocaleString(),
                sender: m.sender.substring(0, 6)
              })));
              // Sort them properly
              const sortedMessages = sortMessagesByTimestamp(parsedMessages);
              // Scroll to bottom after loading messages
              setTimeout(() => scrollToBottom(), 100);
              return sortedMessages;
            } else {
              logger.log('Skipping localStorage load - messages already loaded from blockchain, prevMessages:', prevMessages.length);
              return prevMessages;
            }
          });
        } catch (error) {
          logger.error('Failed to parse stored messages:', error);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, currentAccount?.address, recipientAddress, currentAccount]);

  // Save messages to localStorage whenever messages change (only confirmed messages)
  useEffect(() => {
    if (isConnected && currentAccount && recipientAddress && messages.length > 0) {
      const storageKey = `messages_${currentAccount.address}_${recipientAddress}`;
      // Only save confirmed messages and recent optimistic ones
      const messagesToSave = messages.filter(msg => 
        !msg.id.startsWith('temp-') || // Confirmed messages
        (msg.id.startsWith('temp-') && Date.now() - msg.timestampMs < 60000) // Recent optimistic (1 minute)
      );
      
      localStorage.setItem(storageKey, JSON.stringify(messagesToSave));
      logger.log('Saved messages to localStorage:', messagesToSave.length, 'out of', messages.length);
    }
  }, [messages, isConnected, currentAccount?.address, recipientAddress, currentAccount]);

  // Clear messages when account changes to prevent cross-account contamination
  useEffect(() => {
    setMessages([]);
    setError(null);
    logger.log('Cleared messages due to account change:', currentAccount?.address);
  }, [currentAccount?.address, currentAccount]);

  // Clear messages when recipient changes
  useEffect(() => {
    setMessages([]);
    setError(null);
    logger.log('Cleared messages due to recipient change:', recipientAddress);
  }, [recipientAddress]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim() || !isConnected) return;

    // Use a single timestamp for consistency
    const clientTimestamp = Date.now();
    const tempId = `temp-${clientTimestamp}`;
    setSendStatus(true);

    try {
      logger.log("Sending message to:", recipientAddress);
      logger.log("Current account:", currentAccount?.address);

      if (!recipientAddress || !/^0x[a-fA-F0-9]{64}$/.test(recipientAddress)) {
        throw new Error("Invalid recipient address: " + recipientAddress);
      }

      // Prevent sending to oneself
      if (recipientAddress === currentAccount?.address) {
        throw new Error("Cannot send message to yourself");
      }

      const cleanedMessage = message.trim().replace(/\s+/g, " ");

      // Generate shared encryption key for this conversation
      // For now, derive key from addresses (in production, this should use proper key exchange)
      const sharedKey = await deriveSharedKeyFromAddresses(currentAccount.address, recipientAddress);
      logger.log("Generated shared key for encryption");

      // Encrypt the message
      const encryptedData = await encryptMessage(cleanedMessage, sharedKey);
      logger.log("Message encrypted successfully");

      // Store encrypted message with decentralized fallback strategy
      let storageResult;
      try {
        storageResult = await storeOnWalrus(encryptedData, {
          epochs: 5, // Store for ~5 epochs (~5 days)
          deletable: false
        });
        logger.log("Message stored on Walrus:", storageResult);
      } catch (walrusError) {
        logger.warn("Walrus storage failed, using IPFS decentralized fallback:", walrusError.message);

        // Decentralized fallback: Store on IPFS (fully decentralized)
        try {
          storageResult = await storeOnIPFS(encryptedData);
          storageResult.fallback = true;
          storageResult.method = 'ipfs';
          storageResult.timestamp = clientTimestamp;
          logger.log("Message stored on IPFS:", storageResult);
        } catch (ipfsError) {
          logger.error("IPFS also failed, using compressed Sui fallback:", ipfsError);
          // Ultimate fallback: store compressed version on Sui (minimize cost)
          const compressed = btoa(encryptedData); // Simple base64 encoding for compression
          storageResult = {
            cid: null,
            compressedData: compressed,
            fallback: true,
            method: 'sui-compressed',
            originalSize: encryptedData.length,
            compressedSize: compressed.length,
            timestamp: clientTimestamp
          };
        }
      }

      // Create metadata for Sui blockchain (always small reference)
      const metadata = storageResult.fallback
        ? storageResult.method === 'ipfs'
          ? JSON.stringify({
              cid: storageResult.cid,
              timestamp: clientTimestamp,
              version: "ipfs-fallback-v1",
              size: storageResult.size,
              gatewayUrl: storageResult.gatewayUrl
            })
          : JSON.stringify({
              compressedData: storageResult.compressedData,
              timestamp: clientTimestamp,
              version: "sui-compressed-v1",
              originalSize: storageResult.originalSize,
              compressedSize: storageResult.compressedSize
            })
        : JSON.stringify({
            blobId: storageResult.blobId,
            timestamp: clientTimestamp,
            version: "walrus-v1"
          });

      const metadataBytes = new TextEncoder().encode(metadata);

      const tx = new Transaction();
      tx.moveCall({
        target: `${packageId}::su_messaging::send_message`,
        arguments: [
          tx.pure.address(recipientAddress),
          tx.pure(bcs.vector(bcs.u8()).serialize(Array.from(metadataBytes))),
          tx.object("0x0000000000000000000000000000000000000000000000000000000000000006"), // Clock object
        ],
      });

      logger.log('Sending transaction with storage method:', storageResult.fallback ? `${storageResult.method} fallback` : 'Walrus');
      logger.log('Transaction details:', {
        target: `${packageId}::su_messaging::send_message`,
        recipient: recipientAddress,
        metadataLength: metadataBytes.length,
        storageType: storageResult.fallback ? `${storageResult.method} fallback` : 'walrus',
        blobId: storageResult.blobId || storageResult.cid || 'N/A (fallback storage)'
      });

      // Add message with 'sending' status immediately (show original message for UX)
      const tempMessage = {
        id: tempId,
        sender: currentAccount.address,
        recipient: recipientAddress,
        content: cleanedMessage, // Show original message
        timestamp: new Date(clientTimestamp),
        timestampMs: clientTimestamp,
        clientTimestampMs: clientTimestamp,
        status: 'sending',
        storageType: storageResult.fallback ? `${storageResult.method} fallback` : 'walrus', // Track storage method
      };
      setMessages(prevMessages => {
        const newMessages = [...prevMessages, tempMessage];
        return sortMessagesByTimestamp(newMessages);
      });

      logger.log('About to call signAndExecuteTransactionBlock');

      // Use the mutate function with callbacks
      const isMobile = isMobileDevice();
      signAndExecuteTransactionBlock({
        transaction: tx,
        options: isMobile ? {
          showEffects: true,
          showEvents: true,
        } : {
          showEffects: true,
          showEvents: true,
          showObjectChanges: true,
        },
      }, {
        onSuccess: (result) => {
          logger.log('Transaction result:', result);

          const isSuccess = result && (
            (result.effects && typeof result.effects === 'object' && result.effects.status === 'success') ||
            (result.rawEffects && Array.isArray(result.rawEffects)) ||
            result.digest
          );

          logger.log('Transaction success check:', isSuccess);

          if (isSuccess) {
            logger.log('Transaction completed successfully');

            // Try to extract message info from transaction events
            let messageId = null;
            let eventSender = null;
            let eventRecipient = null;
            let eventTimestamp = null;

            if (result.events && result.events.length > 0) {
              const messageEvent = result.events.find(event =>
                event.type === `${packageId}::su_messaging::MessageCreated` ||
                event.type.includes('MessageCreated')
              );

              if (messageEvent && messageEvent.parsedJson) {
                const eventData = messageEvent.parsedJson;
                messageId = eventData.message_id;
                eventSender = eventData.sender;
                eventRecipient = eventData.recipient;
                eventTimestamp = eventData.timestamp;
                logger.log('Found message event in transaction:', eventData);
              }
            }

            // Update the temporary message with confirmed status
            setMessages(prevMessages => {
              const updatedMessages = prevMessages.map(msg =>
                msg.id === tempId
                  ? {
                      ...msg,
                      id: messageId || `confirmed-${Date.now()}`,
                      sender: eventSender || msg.sender,
                      recipient: eventRecipient || msg.recipient,
                      timestamp: eventTimestamp ? new Date(Long.fromValue(eventTimestamp).toNumber()) : msg.timestamp,
                      blockchainTimestampMs: eventTimestamp ? Long.fromValue(eventTimestamp).toNumber() : null,
                      status: 'confirmed'
                    }
                  : msg
              );

              return sortMessagesByTimestamp(updatedMessages);
            });

            // Write conversation marker to localStorage for Dashboard discovery
            const conversationKey = `conversation_${currentAccount.address}_${recipientAddress}`;
            localStorage.setItem(conversationKey, JSON.stringify({ lastMessageAt: Date.now() }));

            setSendStatus(false);
            setMessage("");
          } else {
            logger.error('Transaction failed:', result);
            setMessages(prevMessages =>
              prevMessages.map(msg =>
                msg.id === tempId
                  ? { ...msg, status: 'failed' }
                  : msg
              )
            );
            setError("Transaction failed: " + (result ? JSON.stringify(result) : 'No result returned'));
            setSendStatus(false);
          }
        },
        onError: (error) => {
          logger.error('Transaction failed with error:', error);

          // Mark the temporary message as failed
          setMessages(prevMessages =>
            prevMessages.map(msg =>
              msg.id === tempId
                ? { ...msg, status: 'failed' }
                : msg
            )
          );

          setError("Failed to send: " + error.message);
          setSendStatus(false);
        }
      });
    } catch (error) {
      logger.error('Error in handleSend:', error);

      // Check if it's a Walrus connectivity issue
      if (error.message.includes('Walrus') || error.message.includes('All Walrus endpoints failed')) {
        setError("Decentralized storage temporarily unavailable. Your message will be stored securely on the blockchain and remain private.");
        // The fallback mechanism will store encrypted content directly on Sui
      } else {
        setError("Failed to send message: " + error.message);
      }

      setSendStatus(false);
    }
  };

  // Decrypt messages that need decryption
  useEffect(() => {
    const decryptPendingMessages = async () => {
      const messagesNeedingDecryption = messages.filter(msg => msg.needsDecryption && !msg.decryptionAttempted);

      if (messagesNeedingDecryption.length === 0) return;

      logger.log(`Decrypting ${messagesNeedingDecryption.length} messages from Walrus`);

      for (const message of messagesNeedingDecryption) {
        try {
          const decryptedContent = await decryptMessageFromStorage(
            message.content,
            message.sender,
            message.recipient
          );

          // Update the message with decrypted content
          setMessages(prevMessages =>
            prevMessages.map(msg =>
              msg.id === message.id
                ? {
                    ...msg,
                    content: decryptedContent,
                    needsDecryption: false,
                    decryptionAttempted: true
                  }
                : msg
            )
          );
        } catch (error) {
          logger.error('Failed to decrypt message:', message.id, error);
          // Mark as attempted but failed
          setMessages(prevMessages =>
            prevMessages.map(msg =>
              msg.id === message.id
                ? {
                    ...msg,
                    content: "[Message temporarily unavailable - stored securely]",
                    needsDecryption: false,
                    decryptionAttempted: true
                  }
                : msg
            )
          );
        }
      }
    };

    decryptPendingMessages();
  }, [messages]);

  // Fetch sender names when messages change
  useEffect(() => {
    if (messages.length > 0) {
      const uniqueSenders = new Set(messages.map(msg => msg.sender));
      uniqueSenders.forEach(sender => {
        if (!senderNames.has(sender)) {
          fetchUserName(sender);
        }
      });
    }
  }, [messages, senderNames, fetchUserName]);

  return (
    <div className="chat-page-wrapper">
      <div className="chat-container">
      {sendStatus && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              fontSize: "40px",
              color: "#ff00ff",
              textShadow: "0 0 20px #00ffff",
              animation: "blink 1s infinite",
            }}
          >
            SENDING...
          </div>
          <style>{`
            @keyframes blink {
              50% { opacity: 0; }
            }
          `}</style>
        </div>
      )}

      <div className="chat-main-row">
        {/* Mobile backdrop */}
        <div className={`chat-sidebar-backdrop ${isMobile && showMobileChatList ? 'open' : ''}`}
             onClick={() => setShowMobileChatList(false)} />

        {/* Sidebar */}
        <div className={`chat-sidebar ${!isMobile || showMobileChatList ? 'open' : ''}`}>
          <div className="chat-header" style={{ backgroundColor: "#330066", borderBottom: "2px solid #ff00ff" }}>
            <h4 style={{ margin: 0, fontSize: "12px", textShadow: "0 0 6px #00ffff" }}>CHAT LIST</h4>
          </div>
          <ListGroup className="chat-sidebar-list">
            {recentChats.map((chat, index) => (
              <ListGroup.Item key={index} action className={`sidebar-chat-item ${chat.address === recipientAddress ? 'active' : ''}`}
                onClick={() => { navigate(`/chat/${chat.address}`); if(isMobile) setShowMobileChatList(false); }}>
                {chat.displayName}
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>

        {/* Main chat area */}
        <div className="chat-main-col">
          {/* Header */}
          <div className="chat-header">
            <button className="sidebar-toggle-btn" onClick={() => setShowMobileChatList(!showMobileChatList)} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              {showMobileChatList ? <RiCloseLine size={18} /> : <RiMessage2Line size={18} />}
            </button>
            <h2 style={{ flex: 1, textAlign: "center" }}>CHATTING WITH {recipientName.toUpperCase()}</h2>
            <button className="scroll-btn" onClick={() => startCall(recipientAddress, recipientName)} title="Voice call" style={{ color: '#00ffff', backgroundColor: 'transparent', border: '1px solid #00ffff', borderRadius: '6px', padding: '3px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '36px', marginRight: '4px' }}>
              <RiPhoneLine size={16} />
            </button>
            <Button onClick={scrollToBottom} style={{ backgroundColor: "#ff00ff", borderColor: "#ff00ff", minHeight: "36px", padding: "3px 8px", fontSize: "14px", display: "flex", alignItems: "center", justifyContent: "center" }}><RiArrowDownLine size={16} /></Button>
          </div>

          {/* Message area */}
          <div ref={chatContentRef} className="chat-messages-area">
            <div style={{ display: "flex", flexDirection: "column", minHeight: "100%" }}>
              {messages.length === 0 ? (
                <div className="empty-state">NO MESSAGES YET. START CHATTING!</div>
              ) : (
                messages.map((msg) => {
                  const isOwn = msg.sender === currentAccount?.address;
                  const statusCls = msg.status === 'sending' || msg.id.startsWith('temp-') ? 'status-sending'
                    : msg.status === 'failed' ? 'status-failed' : msg.status === 'confirmed' ? 'status-confirmed' : '';
                  return (
                    <div key={msg.id} className={`message-bubble ${isOwn ? 'own' : 'received'} ${statusCls}`}>
                      <span className="sender-label">
                        {isOwn ? "YOU" : (senderNames.get(msg.sender) || msg.sender.slice(0, 8) + "...")}:
                      </span>
                      <div className="message-content">{msg.content}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "4px" }}>
                        {(msg.status === 'sending' || msg.id.startsWith('temp-')) && (
                          <span className="status-badge" style={{ color: "#ffff00", display: "flex", alignItems: "center", gap: "4px" }}><RiTimeLine size={12} /> {msg.status === 'sending' ? 'Sending...' : 'Sent'}</span>
                        )}
                        {msg.status === 'confirmed' && (
                          <span className="status-badge" style={{ color: "#00ff00" }}><RiCheckLine size={14} /></span>
                        )}
                        {msg.status === 'failed' && isOwn && (
                          <span className="status-badge" style={{ color: "#ff0000", display: "flex", alignItems: "center", gap: "4px" }}>
                            <RiErrorWarningLine size={12} /> <button onClick={() => setMessages(prev => prev.filter(m => m.id !== msg.id))} style={{ background: "none", border: "1px solid #ff0000", color: "#ff0000", fontSize: "10px", padding: "1px 4px", cursor: "pointer", borderRadius: "2px" }}><RiCloseLine size={10} /></button>
                          </span>
                        )}
                      </div>
                      <small className="message-timestamp">
                        {msg.timestamp instanceof Date && !isNaN(msg.timestamp)
                          ? msg.timestamp.toLocaleString()
                          : "Unknown Time"}
                      </small>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Input area */}
          <div className="chat-input-area">
            <Form onSubmit={handleSend} className="chat-input-form">
              <Form.Control as="textarea" placeholder="TYPE YOUR MESSAGE..."
                value={message} onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend(e)}
                className="message-input"
                style={{ backgroundColor: "#1a0033", color: "#00ffff", fontFamily: "Orbitron, sans-serif" }}
                disabled={!isConnected} />
              <Button type="submit" className="send-button touch-target"
                style={{ backgroundColor: "#ff00ff", borderColor: "#ff00ff", fontFamily: "Orbitron, sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}
                disabled={!isConnected}>
                {isMobile ? <RiSendPlaneLine size={16} /> : <>SEND</>}
              </Button>
            </Form>
            <div className="chat-alerts">
              {!isConnected && <Alert variant="danger" style={{ color: "#ff00ff", backgroundColor: "#330066", border: "1px solid #ff00ff", marginBottom: 0 }}>
                CONNECT YOUR SUI WALLET TO SEND MESSAGES.
              </Alert>}
              {error && <Alert variant="danger" style={{ color: "#ff00ff", backgroundColor: "#330066", border: "1px solid #ff00ff", marginBottom: 0 }}>
                {error}
              </Alert>}
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
  );
}

export default Chat;
